import type React from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack, Box, styled } from "../../../../styled-system/jsx";
import { FaCircleCheck, FaCircleInfo, FaTriangleExclamation, FaLightbulb, FaClock } from "react-icons/fa6";
import { Button } from "../../core/basic/Button";
import { AdvertisementCell } from "../../../modules/advertising/tableCells/AdvertisementCell";
import Pill from "../Pill";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import MetricDisplay, { type Metric } from "./MetricDisplay";

interface InsightAction {
  function: string;
  arguments: string[];
}

interface InsightInfo {
  status: "info" | "success" | "warning";
  content: string;
  metrics?: Metric[];
}

interface InsightIssue {
  content: string;
  metrics?: Metric[];
  actions?: InsightAction[];
}

interface InsightOpportunity {
  content: string;
  metrics?: Metric[];
  actions?: InsightAction[];
}

export interface InsightData {
  id: number;
  title: string;
  description?: string;
  created_on: string;
  metadata?: {
    result?: {
      info?: InsightInfo[];
      issues?: InsightIssue[];
      opportunities?: InsightOpportunity[];
      title?: string;
      status?: string;
      summary?: string;
    };
  };
  entity_type?: string;
  entity_id?: number;
  entity?: Record<string, unknown>;
  status?: string;
}

interface InsightDetailsProps {
  insight: InsightData;
  onActionClick?: (action: InsightAction) => void;
  hideTitle: boolean;
}

const STATUS_LABEL_KEYS = {
  info: "Info",
  success: "Success",
  warning: "Warning",
} as const;

const statusConfig = {
  info: {
    bg: "#EBF5FF",
    text: "#1E40AF",
    borderColor: "#3B82F6",
    icon: FaCircleInfo,
    labelKey: STATUS_LABEL_KEYS.info,
  },
  success: {
    bg: "#F0FDF4",
    text: "#14532D",
    borderColor: "#22C55E",
    icon: FaCircleCheck,
    labelKey: STATUS_LABEL_KEYS.success,
  },
  warning: {
    bg: "#FEF3C7",
    text: "#78350F",
    borderColor: "#F59E0B",
    icon: FaTriangleExclamation,
    labelKey: STATUS_LABEL_KEYS.warning,
  },
};

const Count = styled("span", {
  base: {
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "12px",
    fontWeight: "500",
    backgroundColor: "gray.200",
  },
});

const InfoCard: React.FC<{ info: InsightInfo }> = ({ info }) => {
  const { t } = useTranslation(["chat"]);
  const config = statusConfig[info.status];
  const Icon = config.icon;

  return (
    <Box
      className={css({
        border: "1px solid",
        borderColor: "gray.200",
        borderRadius: "8px",
        width: "100%",
        padding: "medium",
      })}
    >
      <HStack gap="medium" alignItems="flex-start">
        <Icon className={css({ flexShrink: 0, marginTop: "2px", color: config.text, fontSize: "20px" })} />
        <VStack flex="1" gap="medium" justifyContent={"flex-start"} width="full">
          <HStack gap="small" justifyContent="flex-start" alignItems="center" width="full">
            <Pill variant={info.status}>{t(($) => $.chat.insight.status[config.labelKey])}</Pill>
            <span className={css({ color: "#111827", fontSize: "14px", fontWeight: "500" })}>{info.content}</span>
          </HStack>
          <HStack gap="small" justifyContent="space-around" alignItems="flex-start" width="full">
            {info.metrics &&
              info.metrics.length > 0 &&
              info.metrics.map((metric, index) => (
                <Box key={index} className={css({ marginBottom: index < info.metrics!.length - 1 ? "small" : "0" })}>
                  {metric.text && (
                    <span
                      className={css({
                        display: "block",
                        marginBottom: "4px",
                        color: "#4B5563",
                        fontSize: "13px",
                        fontStyle: "italic",
                      })}
                    >
                      {metric.text}
                    </span>
                  )}
                  <MetricDisplay metric={metric} />
                </Box>
              ))}
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
};

const ActionItem: React.FC<{
  item: InsightIssue | InsightOpportunity;
  type: "issue" | "opportunity";
  onActionClick?: (action: InsightAction) => void;
}> = ({ item, type, onActionClick }) => {
  const { t } = useTranslation(["chat"]);
  const hasActions = item.actions && item.actions.length > 0;

  const renderActionLabel = (action: InsightAction): string => {
    const args = action.arguments;
    switch (action.function) {
      case "change_acos_range":
        return t(($) => $.chat.insight.actions["change acos range: {min}% - {max}%"], {
          min: args[0],
          max: args[1],
        });
      case "cap_keyword_bid":
        return t(($) => $.chat.insight.actions["cap keyword bid: ${value}"], { value: args[1] });
      case "cap_target_bid":
        return t(($) => $.chat.insight.actions["cap target bid: ${value}"], { value: args[1] });
      case "negate_search_query":
        return t(($) => $.chat.insight.actions["negate search query: {queries}"], {
          queries: args.join(", "),
        });
      case "bid_boost_keyword":
        return t(($) => $.chat.insight.actions["bid boost keyword"]);
      case "bid_boost_target":
        return t(($) => $.chat.insight.actions["bid boost target"]);
      case "harvest_search_query":
        return t(($) => $.chat.insight.actions["harvest search query: {queries}"], {
          queries: args.join(", "),
        });
      case "enabled_dayparting":
        return t(($) => $.chat.insight.actions["enable dayparting"]);
      case "change_bid_range":
        return t(($) => $.chat.insight.actions["change bid range: ${min} - ${max}"], {
          min: args[0],
          max: args[1],
        });
      case "change_budget":
        return t(($) => $.chat.insight.actions["change budget: {percent}%"], {
          percent: ((args[0] || 0) as unknown as number) * 100,
        });
      default:
        return action.function.replaceAll("_", " ").replaceAll(/\b\w/g, (c) => c.toUpperCase());
    }
  };

  return (
    <Box
      className={css({
        border: "1px solid",
        borderColor: "gray.200",
        borderRadius: "8px",
        width: "100%",
        padding: "medium",
      })}
    >
      <VStack gap="small">
        <HStack gap="small" justifyContent="flex-start" alignItems="flex-start" width="full">
          <Box
            className={css({
              flexShrink: 0,
              border: "2px solid",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              marginTop: "2px",
            })}
            style={{
              borderColor: type === "issue" ? statusConfig.warning.borderColor : statusConfig.success.borderColor,
            }}
          />
          <span className={css({ color: "#111827", fontSize: "14px", fontWeight: "500" })}>{item.content}</span>
        </HStack>
        <HStack gap="small" justifyContent="space-around" alignItems="flex-start" width="full">
          {item.metrics &&
            item.metrics.length > 0 &&
            item.metrics.map((metric, index) => (
              <Box key={index} className={css({ marginBottom: index < item.metrics!.length - 1 ? "small" : "0" })}>
                {metric.text && (
                  <span
                    className={css({
                      display: "block",
                      marginBottom: "4px",
                      color: "#4B5563",
                      fontSize: "13px",
                      fontWeight: "500",
                    })}
                  >
                    {metric.text}
                  </span>
                )}
                {metric.query && (
                  <span
                    className={css({
                      display: "block",
                      marginBottom: "4px",
                      color: "#111827",
                      fontSize: "13px",
                      fontWeight: "600",
                    })}
                  >
                    "{metric.query}"
                  </span>
                )}
                <MetricDisplay metric={metric} />
              </Box>
            ))}
        </HStack>
        {hasActions && (
          <HStack gap="small" marginTop="small">
            {item.actions!.map((action, idx) => (
              <Button
                key={idx}
                size="small"
                variant="secondary"
                onPress={() => onActionClick?.(action)}
                css={{ fontSize: "12px" }}
              >
                {renderActionLabel(action)}
              </Button>
            ))}
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

const InsightDetails: React.FC<InsightDetailsProps> = ({ insight, onActionClick, hideTitle }) => {
  const { t } = useTranslation(["chat"]);
  const fmt = useDateFormatter("shortDateTime");
  const result = insight.metadata?.result;
  const infos = result?.info || [];
  const issues = result?.issues || [];
  const opportunities = result?.opportunities || [];
  const overallStatus = result?.status || "info";
  const statusBadgeConfig = statusConfig[overallStatus as keyof typeof statusConfig];

  return (
    <VStack gap="large" alignItems="stretch" width="100%">
      {/* Header with title and status */}
      {!hideTitle && (
        <VStack gap="small">
          <HStack gap="small" justifyContent="space-between" alignItems="center" width={"100%"}>
            <Pill variant={overallStatus as "info" | "success" | "warning" | "error"}>
              {t(($) => $.chat.insight.status[statusBadgeConfig.labelKey])}
            </Pill>
            <HStack gap="xsmall" alignItems="center" color="#6B7280">
              <FaClock className={css({ fontSize: "12px" })} />
              <span className={css({ fontSize: "12px" })}>
                {t(($) => $.chat["Created {date}"], {
                  date: fmt.format(new Date(insight.created_on)),
                })}
              </span>
            </HStack>
          </HStack>
          <h2 className={css({ margin: "0", color: "#111827", fontSize: "20px", fontWeight: "700" })}>
            {insight.title}
          </h2>
        </VStack>
      )}
      {(insight.description || result?.summary) && (
        <p className={css({ margin: "0", color: "#4B5563", fontSize: "14px", lineHeight: "1.6" })}>
          {insight.description || result?.summary}
        </p>
      )}

      {/* Entity display if exists */}
      {insight.entity_type === "gvadvertisement" && insight.entity && (
        <Box className={css({ borderRadius: "8px", padding: "small", backgroundColor: "#F9FAFB" })}>
          <AdvertisementCell
            ad={{
              ...insight.entity,
              ad_id: insight.entity.id as number,
              ad_name: insight.entity.name as string,
            }}
          />
        </Box>
      )}

      {/* Info Section */}
      {infos.length > 0 && (
        <VStack gap="medium" justifyContent={"flex-start"} width="100%">
          <HStack gap="small" justifyContent="flex-start" alignItems="center" width={"full"}>
            <FaCircleInfo className={css({ color: "#3B82F6", fontSize: "16px" })} />
            <h3 className={css({ margin: "0", color: "#111827", fontSize: "16px", fontWeight: "600" })}>
              {t(($) => $.chat.insight.sections["Info"])} <Count>{infos.length}</Count>
            </h3>
          </HStack>
          <VStack gap="small" width="100%">
            {infos.map((info, index) => (
              <InfoCard key={index} info={info} />
            ))}
          </VStack>
        </VStack>
      )}

      {/* Issues Section */}
      {issues.length > 0 && (
        <VStack gap="medium" width="100%">
          <HStack gap="small" justifyContent="space-between" alignItems="center" width="full">
            <HStack gap="small" alignItems="center">
              <FaTriangleExclamation className={css({ color: "#F59E0B", fontSize: "16px" })} />
              <h3 className={css({ margin: "0", color: "#111827", fontSize: "16px", fontWeight: "600" })}>
                {t(($) => $.chat.insight.sections["Issues"])} <Count>{issues.length}</Count>
              </h3>
            </HStack>
          </HStack>
          <VStack gap="small" width="100%">
            {issues.map((issue, index) => (
              <ActionItem key={index} item={issue} type="issue" onActionClick={onActionClick} />
            ))}
          </VStack>
        </VStack>
      )}

      {/* Opportunities Section */}
      {opportunities.length > 0 && (
        <VStack gap="medium" width="100%">
          <HStack gap="small" justifyContent="space-between" alignItems="center" width="100%">
            <HStack gap="small" alignItems="center">
              <FaLightbulb className={css({ color: "#10B981", fontSize: "16px" })} />
              <h3 className={css({ margin: "0", color: "#111827", fontSize: "16px", fontWeight: "600" })}>
                {t(($) => $.chat.insight.sections["Opportunities"])} <Count>{opportunities.length}</Count>
              </h3>
            </HStack>
          </HStack>
          <VStack gap="small" width="100%">
            {opportunities.map((opportunity, index) => (
              <ActionItem key={index} item={opportunity} type="opportunity" onActionClick={onActionClick} />
            ))}
          </VStack>
        </VStack>
      )}
    </VStack>
  );
};

export default InsightDetails;
